import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { SERVER } from '../config/config';
import ServerImage from './ServerImage';

const Banner = ({ position = 'home-after-stats', className = '' }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const bannerRef = useRef(null);

  useEffect(() => {
    fetchBanners();
  }, [position]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => {
      if (bannerRef.current) {
        observer.unobserve(bannerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (banners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === banners.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [banners.length]);

  const fetchBanners = async () => {
    try {
      // Use the query parameter endpoint to get all banners for the position
      const response = await axios.get(`${SERVER}/banners?position=${position}`);
      
      // The API already returns an array of banners
      const bannersData = response.data || [];
      
      // Sort by display_order (the API already does this, but let's be sure)
      const sortedBanners = bannersData.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      
      setBanners(sortedBanners);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching banners:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading || banners.length === 0) {
    return null;
  }

  return (
    <section 
      ref={bannerRef}
      className={`w-full min-h-[400px] md:min-h-[600px] relative overflow-hidden ${className}`}
    >
      {/* Background Images with Fade Animation */}
      {banners.map((banner, index) => (
        <div 
          key={banner.id || index}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === currentImageIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {banner.image && (
            <ServerImage
              src={banner.image}
              alt={`Banner ${index + 1}`}
              fallback=""
              className="w-full h-[400px] md:h-[600px] object-cover"
            />
          )}
        </div>
      ))}
    </section>
  );
};

export default Banner;